import React, { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { isAfter, isWeekend } from 'date-fns'
import { Play } from 'lucide-react'

import { getT, toISO8601 } from '@fortem-monorepo/js-utilities'
import { DelayRender, useApi } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import { DatePickerWithControls } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { Flows } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { StreamProgress } from '@/components/stream-progress/StreamProgress'
import { FUNDS } from '@/constants/links'
import { GetSummaryStage } from '@/pages/funds/flows/components/GetSummaryStage'
import { GoldCtaStage } from '@/pages/funds/flows/components/GoldCtaStage'
import { LongShortStage } from '@/pages/funds/flows/components/LongShortStage'
import { StreamStageProps } from '@/pages/funds/flows/components/types'
import { useReportStream } from '@/react-query/use-report-stream'

export interface FundsFlowsPageProps {}

export const FundsFlowsPage: React.FC<FundsFlowsPageProps> = ({}) => {
  const [fund_name, setFundName] = useState<string | null>(null)
  const [value_date, setValueDate] = useState(getT())

  const api = useApi()

  const fundsQuery = useQuery({
    queryKey: ['flows', 'funds'],
    queryFn: () => api.flows.getFunds(),
  })

  const reportParams: Flows.FlowReportParams = {
    fund_name: fund_name!,
    value_date: toISO8601(value_date),
  }

  const canRunReport = reportParams.fund_name && !!reportParams.value_date

  const reportStream = useReportStream({
    reportType: 'flows',
    params: reportParams,
    streamFn: api.flows.streamReport,
  })

  return (
    <Page metadata={{ title: 'PM Flows' }} permissions={FUNDS.FLOWS.permissions}>
      <HeaderConfigBar sticky>
        <div className="flex items-center gap-2">
          <SearchSelect
            size="sm"
            loading={fundsQuery.isFetching}
            placeholder={'Select a fund...'}
            value={fund_name ?? ''}
            options={(fundsQuery.data ?? []).map((i) => ({ label: i, value: i }))}
            setValue={(val) => setFundName(val)}
          />
          <DatePickerWithControls
            todayButton={{ enabled: true }}
            disabled={(date) => isAfter(date, getT()) || isWeekend(date)}
            size="sm"
            placeholder={'Report date'}
            selected={value_date}
            onSelect={(date) => date && setValueDate(date)}
          />
          <LoadingButton
            size="sm"
            variant="outline"
            icon={<Play />}
            disabled={!canRunReport}
            onClick={() => {
              return reportStream.startAsync()
            }}
          >
            Run Report
          </LoadingButton>
          <Button
            size="sm"
            variant="outline"
            disabled={!canRunReport}
            onClick={() => reportStream.reset()}
          >
            Reset
          </Button>
        </div>
      </HeaderConfigBar>
      <ViewContainer className={'flex flex-1 gap-4 relative'}>
        {/* Content */}
        <div className={'w-full min-h-full space-y-10'}>
          {!reportStream.isIdle && (
            <StreamProgress
              title={`Flows for ${fund_name} on ${value_date.toDateString()}`}
              stream={reportStream}
            />
          )}
          {reportStream.state.result && (
            <DelayRender
              key={`${reportParams.fund_name ?? 'none'}-${reportParams.value_date}`}
              delay={20}
            >
              {reportStream.state.result.stages.map((stage, stageIndex) => {
                const OutputComponent = getFlowComponent(stage.stage_id) as
                  React.FC<StreamStageProps> | undefined

                return (
                  <div
                    key={stage.stage_name}
                    id={'stage-' + stage.stage_name}
                    className={'border p-10 border-indigo-200 rounded-lg animate-in fade-in'}
                  >
                    <h2 className={'font-medium'}>
                      <span className={'text-indigo-500 font-normal! text-sm mr-2'}>
                        {stageIndex + 1}.
                      </span>
                      {stage.stage_name}
                    </h2>
                    {!!stage.output && OutputComponent && (
                      <div className={'mt-4'}>
                        <OutputComponent output={stage.output} />
                      </div>
                    )}
                  </div>
                )
              })}
            </DelayRender>
          )}
        </div>
      </ViewContainer>
    </Page>
  )
}

const FLOW_COMPONENTS: Record<string, React.FC<StreamStageProps<any>>> = {
  get_summary: GetSummaryStage,
  long_short: LongShortStage,
  gold_cta: GoldCtaStage,
}

const getFlowComponent = (key: string | null) => {
  if (key) {
    const component = FLOW_COMPONENTS?.[key]

    if (component) {
      return component
    }
  }

  return null
}

export interface StreamProgressProps {
  title?: string
  stream: ReturnType<typeof useReportStream>
}
